/**
 * 📝 Register Corporate Account Modal (Admin Direct Registration)
 * Admins can directly register corporate accounts, bypassing approval
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Grid,
  IconButton,
  Step,
  Stepper,
  StepLabel,
  InputAdornment,
  Alert,
} from '@mui/material';
import {
  Close,
  Business,
  Person,
  LocationOn,
  AccountBalance,
} from '@mui/icons-material';
import {
  corporateAccountsService,
  CorporateAccountRegistrationRequest,
} from '@/services/corporateAccountsService';
import { CustomerSelector } from '@/components/bookings/CustomerSelector';
import { User } from '@/services/userService';
import toast from 'react-hot-toast';

interface RegisterCorporateAccountModalProps {
  open: boolean;
  onClose: () => void;
  onRegister: () => void;
}

interface FormData {
  companyName: string;
  registrationNumber: string;
  taxId: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  contactPosition: string;
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  creditLimit: string;
  notes: string;
}

interface FormErrors {
  [key: string]: string;
}

const steps = ['Company Info', 'Contact Person', 'Billing Address', 'Credit & Notes'];

export const RegisterCorporateAccountModal: React.FC<RegisterCorporateAccountModalProps> = ({
  open,
  onClose,
  onRegister,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState<User | null>(null);
  const [formData, setFormData] = useState<FormData>({
    companyName: '',
    registrationNumber: '',
    taxId: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    contactPosition: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'USA',
    creditLimit: '25000',
    notes: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});

  const handleChange = (field: keyof FormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateStep = (step: number): boolean => {
    const newErrors: FormErrors = {};

    switch (step) {
      case 0: // Company Info
        if (!formData.companyName.trim()) {
          newErrors.companyName = 'Company name is required';
        }
        if (!selectedOwner) {
          newErrors.ownerUserId = 'Account owner is required';
        }
        break;

      case 1: // Contact Person
        if (!formData.contactName.trim()) {
          newErrors.contactName = 'Contact name is required';
        }
        if (!formData.contactEmail.trim()) {
          newErrors.contactEmail = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
          newErrors.contactEmail = 'Invalid email format';
        }
        if (!formData.contactPhone.trim()) {
          newErrors.contactPhone = 'Phone number is required';
        }
        break;

      case 2: // Billing Address
        if (!formData.street.trim()) {
          newErrors.street = 'Street address is required';
        }
        if (!formData.city.trim()) {
          newErrors.city = 'City is required';
        }
        if (!formData.state.trim()) {
          newErrors.state = 'State is required';
        }
        if (!formData.zipCode.trim()) {
          newErrors.zipCode = 'ZIP code is required';
        }
        if (!formData.country.trim()) {
          newErrors.country = 'Country is required';
        }
        break;

      case 3: // Credit & Notes
        const limit = parseFloat(formData.creditLimit);
        if (isNaN(limit) || limit <= 0) {
          newErrors.creditLimit = 'Credit limit must be a positive number';
        } else if (limit > 1000000) {
          newErrors.creditLimit = 'Credit limit cannot exceed $1,000,000';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(activeStep)) {
      return;
    }

    if (!selectedOwner) {
      toast.error('Please select an account owner');
      return;
    }

    const requestData: CorporateAccountRegistrationRequest = {
      companyName: formData.companyName,
      billingEmail: formData.contactEmail, // Use contact email as billing email
      registrationNumber: formData.registrationNumber || undefined,
      taxId: formData.taxId || undefined,
      ownerUserId: selectedOwner.id,  // Required by backend
      contactPerson: {
        name: formData.contactName,
        email: formData.contactEmail,
        phone: formData.contactPhone,
        position: formData.contactPosition || undefined,
      },
      billingAddress: {
        street: formData.street,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        country: formData.country,
      },
      creditLimit: parseFloat(formData.creditLimit),
      notes: formData.notes || undefined,
    };

    setSubmitting(true);
    try {
      const response = await corporateAccountsService.registerCorporateAccount(requestData);

      if (response.success) {
        toast.success(`${formData.companyName} has been registered successfully`);
        onRegister();
        handleClose();
      } else {
        toast.error(response.error || 'Failed to register corporate account');
      }
    } catch (error) {
      console.error('Failed to register account:', error);
      toast.error('Failed to register corporate account');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setSelectedOwner(null);
    setFormData({
      companyName: '',
      registrationNumber: '',
      taxId: '',
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      contactPosition: '',
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'USA',
      creditLimit: '25000',
      notes: '',
    });
    setErrors({});
    onClose();
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Business color="primary" />
                <Typography variant="h6">Company Information</Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Company Name"
                value={formData.companyName}
                onChange={handleChange('companyName')}
                error={!!errors.companyName}
                helperText={errors.companyName}
                disabled={submitting}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Registration Number"
                value={formData.registrationNumber}
                onChange={handleChange('registrationNumber')}
                error={!!errors.registrationNumber}
                helperText={errors.registrationNumber || 'Optional'}
                disabled={submitting}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Tax ID"
                value={formData.taxId}
                onChange={handleChange('taxId')}
                error={!!errors.taxId}
                helperText={errors.taxId || 'Optional'}
                disabled={submitting}
              />
            </Grid>
            <Grid item xs={12}>
              <CustomerSelector
                selectedCustomer={selectedOwner}
                onSelectCustomer={setSelectedOwner}
                onCreateNew={() => {
                  // TODO: Open create user modal or redirect to user creation
                  toast('User creation will be implemented in the next iteration', {
                    icon: 'ℹ️',
                  });
                }}
                role="all"
                label="Account Owner"
                placeholder="Search for user by name, email, or phone..."
                helperText={errors.ownerUserId || 'Select the user who will own this corporate account'}
                required={true}
                disabled={submitting}
              />
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Person color="primary" />
                <Typography variant="h6">Contact Person</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Full Name"
                value={formData.contactName}
                onChange={handleChange('contactName')}
                error={!!errors.contactName}
                helperText={errors.contactName}
                disabled={submitting}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Position"
                value={formData.contactPosition}
                onChange={handleChange('contactPosition')}
                error={!!errors.contactPosition}
                helperText={errors.contactPosition || 'Optional'}
                disabled={submitting}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="email"
                label="Email"
                value={formData.contactEmail}
                onChange={handleChange('contactEmail')}
                error={!!errors.contactEmail}
                helperText={errors.contactEmail}
                disabled={submitting}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="tel"
                label="Phone Number"
                value={formData.contactPhone}
                onChange={handleChange('contactPhone')}
                error={!!errors.contactPhone}
                helperText={errors.contactPhone}
                disabled={submitting}
                placeholder="+1-555-0000"
              />
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <LocationOn color="primary" />
                <Typography variant="h6">Billing Address</Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Street Address"
                value={formData.street}
                onChange={handleChange('street')}
                error={!!errors.street}
                helperText={errors.street}
                disabled={submitting}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="City"
                value={formData.city}
                onChange={handleChange('city')}
                error={!!errors.city}
                helperText={errors.city}
                disabled={submitting}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="State / Province"
                value={formData.state}
                onChange={handleChange('state')}
                error={!!errors.state}
                helperText={errors.state}
                disabled={submitting}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="ZIP / Postal Code"
                value={formData.zipCode}
                onChange={handleChange('zipCode')}
                error={!!errors.zipCode}
                helperText={errors.zipCode}
                disabled={submitting}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Country"
                value={formData.country}
                onChange={handleChange('country')}
                error={!!errors.country}
                helperText={errors.country}
                disabled={submitting}
              />
            </Grid>
          </Grid>
        );

      case 3:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AccountBalance color="primary" />
                <Typography variant="h6">Credit Limit & Notes</Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Admin direct registration creates an active account immediately without approval.
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                type="number"
                label="Credit Limit"
                value={formData.creditLimit}
                onChange={handleChange('creditLimit')}
                error={!!errors.creditLimit}
                helperText={errors.creditLimit || 'Maximum credit amount for this account'}
                disabled={submitting}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Quick select:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1, mb: 2 }}>
                {[10000, 25000, 50000, 100000].map((amount) => (
                  <Button
                    key={amount}
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      setFormData((prev) => ({ ...prev, creditLimit: amount.toString() }))
                    }
                    disabled={submitting}
                  >
                    ${amount.toLocaleString()}
                  </Button>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                value={formData.notes}
                onChange={handleChange('notes')}
                error={!!errors.notes}
                helperText={errors.notes || 'Optional internal notes'}
                disabled={submitting}
                placeholder="Add any notes about this corporate account..."
              />
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Register Corporate Account</Typography>
          <IconButton onClick={handleClose} disabled={submitting}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent(activeStep)}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Box sx={{ flex: '1 1 auto' }} />
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={submitting}>
            Back
          </Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button variant="contained" onClick={handleNext} disabled={submitting}>
            Next
          </Button>
        ) : (
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Registering...' : 'Register Account'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
